// Providers.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaToggleOn, FaToggleOff, FaEye } from "react-icons/fa";
import Swal from 'sweetalert2';
import { FaGear } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';

function Providers() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [providers, setProviders] = useState([]);
    const [filteredProviders, setFilteredProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const navigate = useNavigate();

    // Modal states
    const [providerModalOpen, setProviderModalOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchValue: '',
        status: '',
        category: ''
    });

    // Status options
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' }
    ];

    // Category options
    const categoryOptions = [
        { id: '', name: 'All Categories' },
        { id: '1', name: 'Utility' },
        { id: '2', name: 'PG' },
        { id: '3', name: 'Bank' }
    ];

    // IsPrimary options
    const isPrimaryOptions = [
        { id: '0', name: 'No' },
        { id: '1', name: 'Yes' }
    ];

    // Form state for add/edit
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        identity_token: '',
        ip: '',
        status: '1',
        isPrimary: '0'
    });

    // Fetch Providers with pagination
    const fetchProviders = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                searchValue: filters.searchValue,
                status: filters.status,
                category: filters.category,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall(
                "providers",
                params,
                true
            );

            if (result.status === true) {
                setProviders(result.data || []);
                setFilteredProviders(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch providers");
                setProviders([]);
                setFilteredProviders([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setProviders([]);
            setFilteredProviders([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Add Provider
    const handleAddProvider = () => {
        setIsEditMode(false);
        setFormData({
            name: '',
            category: '',
            identity_token: '',
            ip: '',
            status: '1',
            isPrimary: '0'
        });
        setSelectedProvider(null);
        setProviderModalOpen(true);
    };

    // Handle Edit Provider
    const handleEditProvider = (provider) => {
        setIsEditMode(true);
        setSelectedProvider(provider);
        setFormData({
            name: provider.name || '',
            category: provider.category_id || '',
            identity_token: provider.identity_token || '',
            ip: provider.ip || '',
            status: provider.status_id || '1',
            isPrimary: provider.isPrimary_id || '0'
        });
        setProviderModalOpen(true);
    };

    // Handle Delete Provider
    const handleDeleteProvider = (provider) => {
        Swal.fire({
            title: 'Delete Provider?',
            text: `Are you sure you want to delete "${provider.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "delete-provider",
                        {
                            provider_id: provider.id
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success("Provider deleted successfully!");
                        fetchProviders(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to delete provider");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Status Toggle
    const handleStatusToggle = (provider) => {
        const newStatus = provider.status === 'Active' ? '0' : '1';
        const statusName = provider.status === 'Active' ? 'Inactive' : 'Active';

        Swal.fire({
            title: `Toggle Status?`,
            text: `Are you sure you want to change "${provider.name}" to ${statusName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Toggle!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "toggle-provider-status",
                        {
                            provider_id: provider.id,
                            status: newStatus
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success(`Provider ${statusName} successfully!`);
                        fetchProviders(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to toggle status");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Save Provider (Add/Update)
    const handleSaveProvider = async () => {
        if (!formData.name.trim()) {
            toast.error("Please enter provider name");
            return;
        }

        if (!formData.category) {
            toast.error("Please select a category");
            return;
        }

        try {
            const endpoint = isEditMode ? "update-provider" : "add-provider";
            const params = isEditMode
                ? { provider_id: selectedProvider.id, ...formData }
                : formData;

            const response = await apiCall(endpoint, params, true);

            if (response.status === true) {
                toast.success(isEditMode ? "Provider updated successfully!" : "Provider added successfully!");
                setProviderModalOpen(false);
                fetchProviders(pagination.currentPage);
            } else {
                toast.error(response.message || "Failed to save provider");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchProviders(1);
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchProviders(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            status: '',
            category: ''
        });
        fetchProviders(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchProviders(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Get category color
    const getCategoryColor = (category) => {
        const colors = {
            'Payment': 'bg-blue-100 text-blue-800',
            'Recharge': 'bg-green-100 text-green-800',
            'Bill Payment': 'bg-purple-100 text-purple-800',
            'Utility': 'bg-blue-100 text-blue-800',
            'PG': 'bg-green-100 text-green-800',
            'Bank': 'bg-purple-100 text-purple-800'
        };
        return colors[category] || 'bg-gray-100 text-gray-800';
    };

    // Get isPrimary color
    const getIsPrimaryColor = (isPrimary) => {
        const colors = {
            'Yes': 'bg-green-100 text-green-800',
            'No': 'bg-gray-100 text-gray-800'
        };
        return colors[isPrimary] || 'bg-gray-100 text-gray-800';
    };


    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Providers</h1>
                            <p className="text-gray-600 text-sm">View and manage all providers</p>
                        </div>
                        <button
                            onClick={handleAddProvider}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center gap-2"
                        >
                            <FaPlus />
                            Add Provider
                        </button>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {/* Column 1: Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Search
                                    </label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={filters.searchValue}
                                            onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                            placeholder="Search by name, token..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Column 2: Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Column 3: Category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category
                                    </label>
                                    <select
                                        value={filters.category}
                                        onChange={(e) => handleFilterChange('category', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {categoryOptions.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Column 4: Actions */}
                                <div className="flex gap-2 items-end">
                                    <button
                                        onClick={applyFilters}
                                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                    >
                                        Apply
                                    </button>
                                    <button
                                        onClick={resetFilters}
                                        className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredProviders.length}</span> providers
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Providers Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Identity Token
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            IP
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Primary
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredProviders.length > 0 ? (
                                        filteredProviders.map((provider) => (
                                            <tr key={provider.id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                                                    #{provider.id}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {provider.name}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(provider.category)}`}>
                                                        {provider.category}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                                                    {provider.identity_token || 'N/A'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {provider.ip || 'N/A'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getIsPrimaryColor(provider.isPrimary)}`}>
                                                        {provider.isPrimary}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(provider.status)}`}>
                                                            {provider.status}
                                                        </span>
                                                        <button
                                                            onClick={() => handleStatusToggle(provider)}
                                                            className="text-gray-500 hover:text-blue-600 transition-colors"
                                                            title="Toggle Status"
                                                        >
                                                            {provider.status === 'Active' ? (
                                                                <FaToggleOn className="text-2xl text-green-600" />
                                                            ) : (
                                                                <FaToggleOff className="text-2xl text-gray-400" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {provider.date}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleEditProvider(provider)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            title="Edit Provider"
                                                        >
                                                            <FaEdit className="text-sm" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteProvider(provider)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete Provider"
                                                        >
                                                            <FaTrash className="text-sm" />
                                                        </button>
                                                        {provider.category_id === "1" && (
                                                            <button
                                                                onClick={() =>
                                                                    navigate("/api/provider-setup", {
                                                                        state: { provider }
                                                                    })
                                                                }
                                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Provider Setup"
                                                            >
                                                                <FaGear className="text-sm" />
                                                            </button>
                                                        )}
                                                        {provider.category_id === "1" && (
                                                            <button
                                                                onClick={() =>
                                                                    navigate(`/margin/setup`, {
                                                                        state: { provider_id: provider.id },
                                                                    })
                                                                }
                                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Provider Setup"
                                                            >
                                                                <FaEye className="text-sm" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No providers found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Provider Modal */}
            {providerModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setProviderModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
                            {/* Header */}
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {isEditMode ? 'Edit Provider' : 'Add New Provider'}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {isEditMode ? `Editing "${selectedProvider?.name}"` : 'Create a new provider'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setProviderModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Provider Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Enter provider name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="">Select Category</option>
                                            {categoryOptions.filter(opt => opt.id !== '').map(option => (
                                                <option key={option.id} value={option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Identity Token
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.identity_token}
                                            onChange={(e) => setFormData({ ...formData, identity_token: e.target.value })}
                                            placeholder="Enter identity token..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            IP Address
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.ip}
                                            onChange={(e) => setFormData({ ...formData, ip: e.target.value })}
                                            placeholder="Enter IP address..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Is Primary
                                        </label>
                                        <select
                                            value={formData.isPrimary}
                                            onChange={(e) => setFormData({ ...formData, isPrimary: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            {isPrimaryOptions.map(option => (
                                                <option key={option.id} value={option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2">
                                <button
                                    onClick={() => setProviderModalOpen(false)}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveProvider}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                    {isEditMode ? 'Update' : 'Add'} Provider
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default Providers;